/**
 * Unified Rarity System (Single Source of Truth)
 *
 * WICHTIG: Alle Labels und Farben hier ändern!
 * Änderungen wirken sich automatisch auf Joker UND Siegel aus.
 *
 * Verwendung:
 * - Server: const { RARITY } = require('../src/shared/entities/rarity');
 * - Client: import { RARITY, getRarityConfig } from './shared/entities/rarity';
 */

// Rarity Keys (technische IDs - ändern sich NIE)
const RARITY = {
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  LEGENDARY: 'legendary',
  CURSE: 'curse',
  SECRET: 'secret',  // Für versteckte Abzeichen
};

/**
 * Display-Konfiguration für alle Raritäten
 *
 * HIER ÄNDERN für neue Labels, Farben, etc.!
 */
const RARITY_CONFIG = {
  [RARITY.COMMON]: {
    label: 'Pleb',
    color: '#9ca3af',
    glowColor: 'transparent',
    cssClass: 'rarity-common',
    sortOrder: 0,
    animated: false,
    // UI colors for components
    border: '#94a3b8',
    bg: 'white',
    text: '#64748b',
    glow: 'none',
    // Pricing
    sellPrice: 1,
  },
  [RARITY.UNCOMMON]: {
    label: 'Normie',
    color: '#22c55e',
    glowColor: 'rgba(34, 197, 94, 0.4)',
    cssClass: 'rarity-uncommon',
    sortOrder: 1,
    animated: false,
    border: '#22c55e',
    bg: '#f0fdf4',
    text: '#16a34a',
    glow: '0 0 8px rgba(34, 197, 94, 0.3)',
    sellPrice: 2,
  },
  [RARITY.RARE]: {
    label: 'Snob',
    color: '#3b82f6',
    glowColor: 'rgba(59, 130, 246, 0.4)',
    cssClass: 'rarity-rare',
    sortOrder: 2,
    animated: false,
    border: '#3b82f6',
    bg: '#eff6ff',
    text: '#2563eb',
    glow: '0 0 12px rgba(59, 130, 246, 0.4)',
    sellPrice: 3,
  },
  [RARITY.LEGENDARY]: {
    label: 'Präsi',
    color: '#a855f7',
    glowColor: 'rgba(168, 85, 247, 0.5)',
    cssClass: 'rarity-legendary',
    sortOrder: 3,
    animated: true,
    border: '#a855f7',
    bg: '#faf5ff',
    text: '#9333ea',
    glow: '0 0 16px rgba(168, 85, 247, 0.5)',
    sellPrice: 5,
  },
  [RARITY.CURSE]: {
    label: 'Arsch',
    color: '#dc2626',
    glowColor: 'rgba(220, 38, 38, 0.5)',
    cssClass: 'rarity-curse',
    sortOrder: 4,
    animated: true,
    border: '#dc2626',
    bg: '#fef2f2',
    text: '#b91c1c',
    glow: '0 0 12px rgba(220, 38, 38, 0.4)',
    sellPrice: 2,
  },
  [RARITY.SECRET]: {
    label: 'Geheim',
    color: '#FF69B4',
    glowColor: 'rgba(255, 105, 180, 0.5)',
    cssClass: 'rarity-secret',
    sortOrder: 5,
    animated: true,
    border: '#FF69B4',
    bg: '#fff0f5',
    text: '#db2777',
    glow: '0 0 16px rgba(255, 105, 180, 0.5)',
    sellPrice: 0,  // Abzeichen nicht verkaufbar
  },
};

// ========================================
// Sort Order Constants (for direct sorting)
// ========================================

// Ascending: common → legendary (lower = first)
const RARITY_SORT_ASC = {
  [RARITY.COMMON]: 0,
  [RARITY.UNCOMMON]: 1,
  [RARITY.RARE]: 2,
  [RARITY.LEGENDARY]: 3,
  [RARITY.CURSE]: 4,
  [RARITY.SECRET]: 5,
};

// Descending: legendary → common (lower = first)
const RARITY_SORT_DESC = {
  [RARITY.LEGENDARY]: 0,
  [RARITY.RARE]: 1,
  [RARITY.UNCOMMON]: 2,
  [RARITY.COMMON]: 3,
  [RARITY.CURSE]: 4,
  [RARITY.SECRET]: 5,
};

// ========================================
// Helper Functions
// ========================================

/**
 * Get full config for a rarity
 * @param {string} rarity - 'common', 'uncommon', 'rare', or 'legendary'
 * @returns {Object} Full rarity configuration
 */
function getRarityConfig(rarity) {
  return RARITY_CONFIG[rarity] || RARITY_CONFIG[RARITY.COMMON];
}

/**
 * Get German label for a rarity
 * @param {string} rarity
 * @returns {string} German label
 */
function getRarityLabel(rarity) {
  return getRarityConfig(rarity).label;
}

/**
 * Get primary color for a rarity
 * @param {string} rarity
 * @returns {string} CSS color
 */
function getRarityColor(rarity) {
  return getRarityConfig(rarity).color;
}

/**
 * Get CSS class for a rarity
 * @param {string} rarity
 * @returns {string} CSS class name
 */
function getRarityCssClass(rarity) {
  return getRarityConfig(rarity).cssClass;
}

/**
 * Get sell price for a rarity
 * @param {string} rarity
 * @returns {number} Sell price in coins
 */
function getSellPriceForRarity(rarity) {
  return getRarityConfig(rarity).sellPrice;
}

/**
 * Check if a rarity has animation
 * @param {string} rarity
 * @returns {boolean}
 */
function isAnimatedRarity(rarity) {
  return getRarityConfig(rarity).animated;
}

/**
 * Sort items by rarity (higher rarity first)
 * @param {Array} items - Array of items with rarity property
 * @returns {Array} Sorted array
 */
function sortByRarity(items) {
  return [...items].sort((a, b) => {
    const orderA = getRarityConfig(a.rarity).sortOrder;
    const orderB = getRarityConfig(b.rarity).sortOrder;
    return orderB - orderA;
  });
}

/**
 * Get display info object (backwards compatibility)
 * @param {string} rarity
 * @returns {Object} { color, label, cssClass, glow }
 */
function getRarityDisplay(rarity) {
  const config = getRarityConfig(rarity);
  return {
    color: config.color,
    label: config.label,
    cssClass: config.cssClass,
    glow: config.animated ? 'animated' : (config.glow !== 'none' ? 'light' : 'none'),
  };
}

// ========================================
// Exports (CommonJS only - bundler handles ESM)
// ========================================

module.exports = {
  RARITY,
  RARITY_CONFIG,
  RARITY_SORT_ASC,
  RARITY_SORT_DESC,
  getRarityConfig,
  getRarityLabel,
  getRarityColor,
  getRarityCssClass,
  getSellPriceForRarity,
  isAnimatedRarity,
  sortByRarity,
  getRarityDisplay,
};
